/**
 * SortableMaterialsGrid.jsx
 * Griglia materiali con drag & drop per riordinamento
 * Usa @dnd-kit per gestire il drag and drop
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  ExternalLink,
  MessageSquare,
  Trash2
} from 'lucide-react';

/**
 * Singolo item draggabile
 */
const SortableMaterialItem = ({ 
  pm, 
  onOpenNotes, 
  onDelete,
  isDragging = false
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isItemDragging,
  } = useSortable({ id: pm.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isItemDragging ? 0.5 : 1,
    zIndex: isItemDragging ? 50 : 'auto',
  };

  const material = pm.material || {};
  const image = pm.material_image || material.image_url;
  const name = pm.material_name || material.name || 'Materiale';
  const type = pm.material_type || material.category || 'Stone';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative bg-white/5 rounded-xl overflow-hidden border transition-all ${
        isItemDragging 
          ? 'border-[#b08b5c] shadow-xl shadow-[#b08b5c]/20' 
          : 'border-white/10 hover:border-[#b08b5c]/30'
      }`}
      data-testid={`project-material-${pm.id}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-3 left-3 z-10 p-1.5 rounded-lg bg-black/60 backdrop-blur-sm text-white/40 hover:text-white cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        title="Trascina per riordinare"
      >
        <GripVertical className="w-4 h-4" />
      </div>

      {/* Order Badge */}
      <div className="absolute top-3 left-12 z-10 w-6 h-6 rounded-full bg-[#b08b5c]/80 text-white text-xs font-medium flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        {pm.sort_order + 1}
      </div>

      {/* Image */}
      <div className="aspect-square overflow-hidden">
        <img
          src={image || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80'}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-white font-medium truncate mb-1">{name}</h3>
        <p className="text-white/40 text-sm">{type}</p>
        
        {pm.notes && (
          <p className="text-[#b08b5c] text-xs mt-2 line-clamp-2 italic">{pm.notes}</p>
        )}
      </div>

      {/* Actions */}
      <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {pm.material_id && (
          <Link
            to={`/materials/${pm.material_id}`}
            className="p-2 rounded-lg bg-black/60 backdrop-blur-sm text-white/80 hover:text-white"
            title="Vedi dettaglio"
          >
            <ExternalLink className="w-4 h-4" />
          </Link>
        )}
        <button
          onClick={() => onOpenNotes(pm)}
          className="p-2 rounded-lg bg-black/60 backdrop-blur-sm text-white/80 hover:text-white"
          title="Aggiungi nota"
        >
          <MessageSquare className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(pm)}
          className="p-2 rounded-lg bg-black/60 backdrop-blur-sm text-red-400 hover:text-red-300"
          title="Rimuovi"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Overlay durante il drag (preview dell'item)
 */
const DragPreview = ({ pm }) => {
  if (!pm) return null;

  const material = pm.material || {};
  const image = pm.material_image || material.image_url;
  const name = pm.material_name || material.name || 'Materiale';

  return (
    <div className="w-64 bg-[#0a0a0f] rounded-xl overflow-hidden border-2 border-[#b08b5c] shadow-2xl shadow-[#b08b5c]/30 opacity-90">
      <div className="aspect-square overflow-hidden">
        <img
          src={image || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80'}
          alt={name}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-3 bg-black/80">
        <p className="text-white font-medium text-sm truncate">{name}</p>
      </div>
    </div>
  );
};

/**
 * Griglia principale con drag & drop
 */
const SortableMaterialsGrid = ({
  materials,
  onReorder,
  onOpenNotes,
  onDelete
}) => {
  const [activeId, setActiveId] = React.useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimo movimento prima di iniziare il drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = materials.findIndex((m) => m.id === active.id);
      const newIndex = materials.findIndex((m) => m.id === over.id);
      
      const newOrder = arrayMove(materials, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const activeItem = activeId ? materials.find((m) => m.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={materials.map((m) => m.id)}
        strategy={rectSortingStrategy}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {materials.map((pm) => (
            <SortableMaterialItem
              key={pm.id}
              pm={pm}
              onOpenNotes={onOpenNotes}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay adjustScale={false}>
        {activeItem ? <DragPreview pm={activeItem} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

export default SortableMaterialsGrid;
